import { transferDutyDefaults } from '@/data/defaults'
import type { CalculatorContext } from '@/types/calculator'
import { roundCents } from '@/utils/money'
import type { TransferCostInputs, TransferCostResult } from './types'

/**
 * Transfer duty per the SARS sliding-scale brackets. Since 23 February 2011 the
 * same rates apply to natural persons, companies, close corporations and trusts.
 * Each bracket's rate applies to the value above the previous bracket's ceiling.
 */
function calculateTransferDuty(price: number): number {
  const bracket = transferDutyDefaults.brackets.find(
    (b) => price >= b.min && (b.max === null || price <= b.max)
  )
  if (!bracket) return 0
  const excess = price - Math.max(bracket.min - 1, 0)
  return bracket.base + bracket.rate * excess
}

function conveyancingFee(price: number): number {
  // Simplified tiered estimate based on property value
  if (price <= 1_000_000) return 16_000
  if (price <= 2_000_000) return 20_000
  if (price <= 3_000_000) return 24_000
  if (price <= 5_000_000) return 30_000
  return 40_000
}

function bondRegistrationFee(loanAmount: number): number {
  if (loanAmount <= 0) return 0
  if (loanAmount <= 1_000_000) return 14_000
  if (loanAmount <= 2_000_000) return 18_000
  if (loanAmount <= 3_000_000) return 22_000
  return 28_000
}

export function calculateTransferCost(
  inputs: TransferCostInputs,
  _context: CalculatorContext
): TransferCostResult {
  const transferDuty = calculateTransferDuty(inputs.purchasePrice)
  const conveyancing = conveyancingFee(inputs.purchasePrice)
  const deedsOffice = 1_200
  const deedsSearch = 500
  const postage = 1_000
  const bondReg = inputs.financed ? bondRegistrationFee(inputs.loanAmount) : 0

  return {
    transferDuty: roundCents(transferDuty),
    conveyancingFee: roundCents(conveyancing),
    deedsOfficeFee: roundCents(deedsOffice),
    bondRegistrationFee: roundCents(bondReg),
    deedsSearchFee: roundCents(deedsSearch),
    postagePetties: roundCents(postage),
    totalCashRequired: roundCents(
      transferDuty + conveyancing + deedsOffice + bondReg + deedsSearch + postage
    ),
  }
}
